import $http from '@/api/http'
import * as qs from 'qs'

interface State {
  myAge: number
  myBio: string
  myCharm: number
  myD: string
  myFuken: number
  myGender: string
  myPoint: number
  myProfileImageURL: string
  myJob: number
  myM: string
  myName: string
  myShutubotu: number
  myType: number
  myY: string
  myUserId: string
  profImageConfirmTimestamp: number
  isInReviewProfileImage: boolean
  isFirstTimeDisplayProfileEdit: boolean
  referCode: string
  deviceToken: string
  voipDeviceToken: string
}

interface MyProfilePayload {
  bio: string
  charm: number
  d: string
  fuken: number
  gender: string
  job: number
  m: string
  name: string
  profile_image_url: string
  shutubotu: number
  type: number
  y: string
  point: number
}

const defaultState: State = {
  myAge: -1,
  myBio: '',
  myCharm: 0,
  myD: '',
  myFuken: 0,
  myGender: '1',
  myJob: 0,
  myM: '',
  myName: '',
  myPoint: 0,
  myProfileImageURL: '',
  myShutubotu: 0,
  myType: 0,
  myY: '',
  myUserId: '',
  profImageConfirmTimestamp: 0,
  isInReviewProfileImage: false,
  isFirstTimeDisplayProfileEdit: false,
  referCode: '',
  deviceToken: '',
  voipDeviceToken: ''
}

const state: State = Object.assign({}, defaultState)

const mutations = {
  resetState(state: State) {
    const s = defaultState
    Object.keys(s).forEach(key => {
      state[key] = s[key]
    })
  },

  setMyAge(state: State, myAge: number) {
    state.myAge = myAge
  },
  setMyName(state: State, myName: string) {
    state.myName = myName
  },
  setMyUserId(state: State, myId: string) {
    state.myUserId = myId
  },
  // NOTE: 登録時に誤って数値として設定していたのでしばらくは強制的に文字列に変換させる
  // 2018年 9月27日 木曜日 18時00分15秒 JST
  setMyGender(state: State, myGender: string) {
    if (typeof myGender === 'number') {
      state.myGender = myGender + ''
    } else {
      state.myGender = myGender
    }
  },
  setMyPoint(state: State, myPoint: number) {
    state.myPoint = myPoint
  },
  setMyProfileImageURL(state: State, myProfileImageURL: string) {
    state.myProfileImageURL = myProfileImageURL
  },
  setProfImageConfirmTimestamp(state: State, profImageConfirmTimestamp: number) {
    state.profImageConfirmTimestamp = profImageConfirmTimestamp
  },
  setIsInReviewProfileImage(state: State, isInReview: boolean) {
    state.isInReviewProfileImage = isInReview
  },
  setIsFirstTimeDisplayProfileEdit(state: State, isFirstTimeDisplayProfileEdit: boolean) {
    state.isFirstTimeDisplayProfileEdit = isFirstTimeDisplayProfileEdit
  },
  setReferCode(state: State, referCode: string) {
    state.referCode = referCode
  },
  setDeviceToken(state: State, token: string) {
    state.deviceToken = token
  },
  setVoipDeviceToken(state: State, token: string) {
    state.voipDeviceToken = token
  },

  // プロフィールの一括セット
  setMyProfile(state: State, payload: MyProfilePayload) {
    state.myBio = payload.bio
    state.myCharm = payload.charm
    state.myD = payload.d
    state.myFuken = payload.fuken
    // NOTE: Singup.vue で誤って数値として Store に保存していたのでしばらくは強制的に文字列に変換させる
    // 2018年 9月27日 木曜日 18時00分15秒 JST
    state.myGender = typeof payload.gender === 'number' ? payload.gender + '' : payload.gender
    state.myJob = payload.job
    state.myM = payload.m
    state.myName = payload.name
    state.myShutubotu = payload.shutubotu
    state.myType = payload.type
    state.myY = payload.y
    state.myPoint = payload.point
  }
}

const actions = {
  /**
   * 自分のプロフィールを取得
   */
  async getMyProfile({ commit }) {
    try {
      const res = await $http.post(
        '/profile_setting.php',
        qs.stringify({
          page: 'get_init'
        })
      )
      if (res.data.error) {
        throw new Error(res.data.error)
      }
      commit('setIsInReviewProfileImage', res.data.user_data.is_in_review)
      commit('setMyName', res.data.user_data.name)
      commit('setMyPoint', res.data.user_data.point)
      commit('setMyProfileImageURL', res.data.user_data.profile_image_url)
      commit('setMyProfile', res.data.user_data)
      return
    } catch (err) {
      console.error(err)
    }
  },

  /**
   * 自分のプロフィールを更新
   */
  updateMyProfile({ commit }, payload: MyProfilePayload): Promise<string> {
    return new Promise(async (resolve, reject) => {
      // YYYY-MM-DD 形式の文字列を year,month,day に分割
      try {
        const params = {
          page: 'save'
        }
        for (const key of Object.keys(payload)) {
          params[key] = payload[key]
        }
        const res = await $http.post('/profile_setting.php', qs.stringify(params))
        if (res.data.error) {
          throw new Error(res.data.error)
        }
        commit('setMyProfile', payload)
        resolve(res.data.id)
      } catch (err) {
        reject(err)
      }
    })
  },

  /**
   * 自分のプロフィール画像を取得
   */
  async getMyProfileImage({ commit }) {
    try {
      const res = await $http.post(
        '/profile_setting.php',
        qs.stringify({
          page: 'get_init'
        })
      )
      if (res.data.error) {
        throw new Error(res.data.error)
      }
      commit('setMyImageURL', res.data.image)
    } catch (err) {
      console.error(err)
    }
  }
}

const getters = {
  /**
   * 自分の Profile が初期化されているか判定
   */
  initializedMyProfile: (state: State): boolean => {
    if (
      state.myName &&
      state.myName.length > 0 &&
      state.myGender &&
      state.myY &&
      state.myY.length > 0 &&
      state.myM &&
      state.myM.length > 0 &&
      state.myD &&
      state.myD.length > 0
    ) {
      return true
    } else {
      return false
    }
  },
  myAge: (state: State): number => state.myAge,
  myProfileImageURL: (state: State): string => state.myProfileImageURL,
  myBio: (state: State): string => state.myBio,
  myCharm: (state: State): number => state.myCharm,
  myD: (state: State): string => state.myD,
  myFuken: (state: State): number => state.myFuken,
  // NOTE: 登録時に誤って数値として設定していたのでしばらくは強制的に文字列に変換させる
  // 2018年 9月27日 木曜日 18時00分15秒 JST
  myGender: (state: State): string => {
    return typeof state.myGender === 'number' ? state.myGender + '' : state.myGender
  },
  myGenderLabel: (state: State): string =>
    state.myGender === '1' ? '男性' : state.myGender === '2' ? '女性' : '',
  isMan: (state): boolean => state.myGender === '1',
  isWoman: (state): boolean => state.myGender === '2',
  myJob: (state: State): number => state.myJob,
  myM: (state: State): string => state.myM,
  myName: (state: State): string => state.myName,
  myUserId: (state: State): string => state.myUserId,
  myPoint: (state: State): number => state.myPoint,
  myShutubotu: (state: State): number => state.myShutubotu,
  myType: (state: State): number => state.myType,
  myY: (state: State): string => state.myY,
  profImageConfirmTimestamp: (state: State): number => state.profImageConfirmTimestamp,
  isInReviewProfileImage: (state: State): boolean => state.isInReviewProfileImage,
  isFirstTimeDisplayProfileEdit: (state: State): boolean => state.isFirstTimeDisplayProfileEdit,
  referCode: (state: State): string => state.referCode,
  deviceToken: (state: State): string => state.deviceToken,
  voipDeviceToken: (state: State): string => state.voipDeviceToken
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
